import { useState } from "react";
import { useAuth, apiError } from "../context/AuthContext";
import { KeyRound, Loader2, LogOut } from "lucide-react";

/**
 * ForceChangePassword — full-screen blocker shown when the logged-in user
 * has `must_change_password: true`. Cannot be dismissed. Rendered above the
 * app shell in App.js. Keeps the API safe: pwd_reset / access-with-flag
 * tokens can only call /auth/change-password anyway.
 */
export default function ForceChangePassword() {
  const { user, changePassword, logout } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("Please choose a password with at least 8 characters.");
      return;
    }
    if (newPassword !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      await changePassword({ new_password: newPassword });
      // AuthProvider clears the flag once the new access token comes back.
      window.location.href = "/";
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Password change failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-950/60 backdrop-blur-sm flex items-center justify-center px-4" data-testid="force-pwd-screen">
      <div className="w-full max-w-md bg-[#FAFAF9] border border-stone-200 rounded-lg shadow-2xl p-8">
        <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-semibold text-stone-500">
          <KeyRound className="h-3.5 w-3.5" /> Set a new password
        </div>
        <h2 className="font-heading text-2xl font-normal tracking-tight text-stone-900 mt-2">
          Choose a new password
        </h2>
        <p className="text-sm text-stone-500 mt-2">
          {user?.email} is on the default password. Please set a new one before continuing.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="force-pwd-form">
          <div>
            <label className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-500">New password</label>
            <input
              type="password"
              required
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="force-pwd-new"
              className="mt-1.5 w-full border border-stone-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/70"
              placeholder="At least 8 characters"
              autoFocus
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.15em] uppercase font-semibold text-stone-500">Confirm new password</label>
            <input
              type="password"
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="force-pwd-confirm"
              className="mt-1.5 w-full border border-stone-300 rounded-md px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/70"
              placeholder="Re-enter password"
            />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="force-pwd-error">{error}</p>}
          <button
            type="submit"
            disabled={busy}
            data-testid="force-pwd-submit"
            className="w-full bg-stone-900 hover:bg-stone-800 text-white rounded-md py-2.5 font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
          </button>
          <button
            type="button"
            onClick={logout}
            data-testid="force-pwd-logout"
            className="w-full text-stone-500 hover:text-stone-900 text-xs flex items-center justify-center gap-1.5 pt-1"
          >
            <LogOut className="h-3 w-3" /> Sign out instead
          </button>
        </form>
      </div>
    </div>
  );
}
